import type { PermissionContextDto } from "@Front/Api/LeCoffreApi/SuperAdmin/RolePermissions/RolePermissions";
import { sanitizeMatrixKey } from "le-coffre-resources/dist/Shared/rolePermissionsMatrixNormalize.js";
import {
	resolveRealOfficeMatrixRoleFromOfficeRoleLabel,
	shouldResolveMatrixRoleAsGuestNotary,
} from "le-coffre-resources/dist/Shared/guestNotaryMatrixRoleResolution.js";
import { isSuperAdminRole } from "@Front/Utils/officeRoleStatusHelpers";

/**
 * Same mapping as backend `resolveUserRoleForPermissions` / `roleNormalizer.ts`.
 * The API returns a matrix slice for this role key only.
 */
const ROLE_DIRECT_MAP: Record<string, string> = {
	super_admin: "super-admin",
	"super-admin": "super-admin",
	admin: "admin",
	collaborator: "collaborator",
	collaborateur: "collaborator",
	notaire: "notary",
	invite: "guest_notary",
};

function resolveFromNormalizedRoleAndOffice(
	normalizedRole: string,
	normalizedOfficeRole: string,
	activeOfficeUid: string,
): string | null {
	if (shouldResolveMatrixRoleAsGuestNotary(activeOfficeUid, normalizedOfficeRole)) {
		return "guest_notary";
	}
	const direct = ROLE_DIRECT_MAP[normalizedRole];
	if (direct !== undefined) {
		return direct;
	}
	if (normalizedRole === "notary") {
		return resolveRealOfficeMatrixRoleFromOfficeRoleLabel(normalizedOfficeRole) ?? "notary";
	}
	return resolveRealOfficeMatrixRoleFromOfficeRoleLabel(normalizedOfficeRole) ?? normalizedRole;
}

/**
 * Role key used to read `GET /notary/my-permissions` filtered matrix entries.
 * Must match backend `resolveUserRoleForPermissions` (not UI affiliation overrides).
 */
export function resolveMatrixEvaluationRole(permissionContext: PermissionContextDto | null | undefined): string | null {
	if (!permissionContext) {
		return null;
	}
	if (permissionContext.isSuperAdmin === true) {
		return "super-admin";
	}
	const globalRole = permissionContext.globalRole?.trim();
	if (!globalRole) {
		return null;
	}
	const normalizedRole = sanitizeMatrixKey(globalRole);
	const normalizedOfficeRole = sanitizeMatrixKey(permissionContext.officeRole ?? "");
	const activeOfficeUid = sanitizeMatrixKey(permissionContext.activeOfficeUid ?? "");
	return resolveFromNormalizedRoleAndOffice(normalizedRole, normalizedOfficeRole, activeOfficeUid);
}

export function isSuperAdminPermissionContext(permissionContext: PermissionContextDto | null | undefined): boolean {
	if (!permissionContext) {
		return false;
	}
	if (permissionContext.isSuperAdmin === true) {
		return true;
	}
	return isSuperAdminRole(permissionContext.globalRole);
}
